import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Search, Filter, ArrowUpDown, ArrowDownToLine, CheckCircle, RefreshCw } from 'lucide-react';
import { SortSelect, sortByField } from '../../components/SortSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IHALE_USULLERI = [
  { value: 'acik', label: 'Açık İhale' },
  { value: 'belli_istekliler', label: 'Belli İstekliler Arasında' },
  { value: 'pazarlik_21a', label: 'Pazarlık 21/a' },
  { value: 'pazarlik_21b', label: 'Pazarlık 21/b' },
  { value: 'pazarlik_21c', label: 'Pazarlık 21/c' },
  { value: 'pazarlik_21d', label: 'Pazarlık 21/d' },
  { value: 'pazarlik_21e', label: 'Pazarlık 21/e' },
];

const SOZLESME_TURLERI = [
  { value: 'birim_fiyat', label: 'Birim Fiyat' },
  { value: 'anahtar_teslim', label: 'Anahtar Teslimi Götürü Bedel' },
  { value: 'karma', label: 'Karma' },
];

const IS_GRUPLARI = [
  { value: 'yeni_insaat', label: 'Yeni İnşaat' },
  { value: 'tadilat_onarim', label: 'Tadilat-Onarım' },
  { value: 'altyapi', label: 'Altyapı' },
  { value: 'ustyapi', label: 'Üstyapı' },
  { value: 'restorasyon', label: 'Restorasyon' },
  { value: 'tesisat', label: 'Tesisat' },
  { value: 'cevre_duzenleme', label: 'Çevre Düzenleme' },
];

const ASAMALAR = [
  { kod: 'planlama', ad: 'Planlama & Keşif', mod: 'ihale' },
  { kod: 'mukayeseli_kesif', ad: 'Mukayeseli Keşif', mod: 'ihale' },
  { kod: 'ihale_dokumani', ad: 'İhale Dokümanı', mod: 'ihale' },
  { kod: 'ilan', ad: 'İlan / Davet', mod: 'ihale' },
  { kod: 'teklif_toplama', ad: 'Teklif Toplama', mod: 'ihale' },
  { kod: 'teklif_degerlendirme', ad: 'Teklif Değerlendirme', mod: 'ihale' },
  { kod: 'ihale_karari', ad: 'İhale Kararı', mod: 'ihale' },
  { kod: 'onay_kesinlesme', ad: 'Onay / Kesinleşme', mod: 'ihale' },
  { kod: 'sozlesme_imzasi', ad: 'Sözleşme İmzası', mod: 'ihale' },
  { kod: 'yer_teslimi', ad: 'Yer Teslimi', mod: 'sozlesme' },
  { kod: 'insaat_baslangic', ad: 'İnşaat Başlangıcı', mod: 'sozlesme' },
  { kod: 'insaat_uygulama', ad: 'İnşaat / Uygulama', mod: 'sozlesme' },
  { kod: 'hakedisler', ad: 'Hakedişler & İş Takibi', mod: 'sozlesme' },
  { kod: 'gecici_kabul', ad: 'Geçici Kabul', mod: 'sozlesme' },
  { kod: 'garanti_suresi', ad: 'Garanti Süresi', mod: 'sozlesme' },
  { kod: 'kesin_kabul', ad: 'Kesin Kabul', mod: 'sozlesme' },
  { kod: 'teminat_iadesi', ad: 'Teminat İadesi & Kapanış', mod: 'sozlesme' },
];

const ASAMA_COLORS = {
  planlama: '#6b7280', mukayeseli_kesif: '#8b5cf6', ihale_dokumani: '#6366f1',
  ilan: '#3b82f6', teklif_toplama: '#0ea5e9', teklif_degerlendirme: '#06b6d4',
  ihale_karari: '#14b8a6', onay_kesinlesme: '#10b981', sozlesme_imzasi: '#22c55e',
  yer_teslimi: '#c9a227', insaat_baslangic: '#f59e0b', insaat_uygulama: '#f97316',
  hakedisler: '#ef4444', gecici_kabul: '#ec4899', garanti_suresi: '#a855f7',
  kesin_kabul: '#2d7a4f', teminat_iadesi: '#1e3a5f'
};

const formatMoney = (val) => {
  if (!val && val !== 0) return '-';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(val);
};

const emptyForm = {
  ikn: '', is_adi: '', yaklasik_maliyet: '', ihale_usulu: '', sozlesme_turu: '',
  is_grubu: '', mevcut_asama: 'planlama', ihale_tarihi: '', sozlesme_tarihi: '',
  sozlesme_bedeli: '', sozlesme_suresi_gun: '', yer_teslim_tarihi: '',
  is_baslangic_tarihi: '', orijinal_bitis_tarihi: '', revize_bitis_tarihi: '',
  garanti_suresi_ay: '12', sorumlu_personel: '', kontrol_teskilati: [],
  yuklenici_adi: '', notlar: '',
  gecici_kabul_tarihi: '', kesin_kabul_tarihi: ''
};

export default function ISYIhaleler({ ihaleler, onRefresh, isAdmin, users = [] }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAsama, setFilterAsama] = useState('all');
  const [filterTur, setFilterTur] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aktarDialogOpen, setAktarDialogOpen] = useState(false);
  const [erpIhaleler, setErpIhaleler] = useState([]);
  const [aktarilanIds, setAktarilanIds] = useState(new Set());
  const [aktarmaLoading, setAktarmaLoading] = useState(false);
  const [topluAktarLoading, setTopluAktarLoading] = useState(false);
  const [senkronizeLoading, setSenkronizeLoading] = useState(false);
  const [sortKey, setSortKey] = useState('newest');

  // Aktarılmış ihale id'lerini topla
  const refreshAktarilanIds = () => {
    const ids = new Set(ihaleler.filter(i => i.erp_kaynak_id).map(i => i.erp_kaynak_id));
    setAktarilanIds(ids);
  };

  const fetchErpIhaleler = async () => {
    try {
      const res = await axios.get(`${API}/isy/erp-ihaleler`, { withCredentials: true });
      setErpIhaleler(res.data);
      refreshAktarilanIds();
    } catch (err) { toast.error('İhaleler yüklenemedi'); }
  };

  const handleTekAktar = async (erpId) => {
    setAktarmaLoading(true);
    try {
      await axios.post(`${API}/isy/ihaleler/erp-aktar`, { erp_ihale_id: erpId }, { withCredentials: true });
      setAktarilanIds(prev => new Set([...prev, erpId]));
      toast.success('İhale aktarıldı');
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aktarma hatası'); }
    finally { setAktarmaLoading(false); }
  };

  const handleTopluAktar = async () => {
    setTopluAktarLoading(true);
    try {
      const res = await axios.post(`${API}/isy/ihaleler/erp-toplu-aktar`, {}, { withCredentials: true });
      toast.success(`${res.data.aktarilan} ihale aktarıldı, ${res.data.atlanan} atlandı`);
      onRefresh();
      fetchErpIhaleler();
    } catch (err) { toast.error(err.response?.data?.detail || 'Toplu aktarma hatası'); }
    finally { setTopluAktarLoading(false); }
  };

  const handleSenkronize = async () => {
    setSenkronizeLoading(true);
    try {
      const res = await axios.post(`${API}/isy/ihaleler/erp-senkronize`, {}, { withCredentials: true });
      toast.success(`${res.data.guncellenen} ihale güncellendi${res.data.hatali > 0 ? `, ${res.data.hatali} hata` : ''}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Senkronizasyon hatası'); }
    finally { setSenkronizeLoading(false); }
  };

  const openCreate = () => { setFormData({ ...emptyForm }); setEditMode(false); setSelectedId(null); setDialogOpen(true); };
  
  const openEdit = (ihale) => {
    setFormData({
      ikn: ihale.ikn || '', is_adi: ihale.is_adi || '',
      yaklasik_maliyet: ihale.yaklasik_maliyet?.toString() || '',
      ihale_usulu: ihale.ihale_usulu || '', sozlesme_turu: ihale.sozlesme_turu || '',
      is_grubu: ihale.is_grubu || '', mevcut_asama: ihale.mevcut_asama || 'planlama',
      ihale_tarihi: ihale.ihale_tarihi || '', sozlesme_tarihi: ihale.sozlesme_tarihi || '',
      sozlesme_bedeli: ihale.sozlesme_bedeli?.toString() || '',
      sozlesme_suresi_gun: ihale.sozlesme_suresi_gun?.toString() || '',
      yer_teslim_tarihi: ihale.yer_teslim_tarihi || '',
      is_baslangic_tarihi: ihale.is_baslangic_tarihi || '',
      orijinal_bitis_tarihi: ihale.orijinal_bitis_tarihi || '',
      revize_bitis_tarihi: ihale.revize_bitis_tarihi || '',
      garanti_suresi_ay: ihale.garanti_suresi_ay?.toString() || '12',
      sorumlu_personel: ihale.sorumlu_personel || '',
      kontrol_teskilati: ihale.kontrol_teskilati || [],
      yuklenici_adi: ihale.yuklenici_adi || '', notlar: ihale.notlar || '',
      gecici_kabul_tarihi: ihale.gecici_kabul_tarihi || '',
      kesin_kabul_tarihi: ihale.kesin_kabul_tarihi || ''
    });
    setEditMode(true); setSelectedId(ihale.id); setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.is_adi.trim()) { toast.error('İşin adı zorunludur'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        yaklasik_maliyet: formData.yaklasik_maliyet ? parseFloat(formData.yaklasik_maliyet) : null,
        sozlesme_bedeli: formData.sozlesme_bedeli ? parseFloat(formData.sozlesme_bedeli) : null,
        sozlesme_suresi_gun: formData.sozlesme_suresi_gun ? parseInt(formData.sozlesme_suresi_gun) : null,
        garanti_suresi_ay: formData.garanti_suresi_ay ? parseInt(formData.garanti_suresi_ay) : 12,
      };
      if (editMode) {
        await axios.put(`${API}/isy/ihaleler/${selectedId}`, payload, { withCredentials: true });
        toast.success('İhale güncellendi');
      } else {
        await axios.post(`${API}/isy/ihaleler`, payload, { withCredentials: true });
        toast.success('İhale oluşturuldu');
      }
      setDialogOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata oluştu'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/ihaleler/${deleteId}`, { withCredentials: true });
      toast.success('İhale silindi'); setDeleteId(null); onRefresh();
    } catch (e) { toast.error('Silme hatası'); }
  };

  const filtered = (ihaleler || []).filter(ih => {
    const matchSearch = !searchTerm || ih.is_adi?.toLowerCase().includes(searchTerm.toLowerCase()) || ih.ikn?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchAsama = filterAsama === 'all' || ih.mevcut_asama === filterAsama;
    const matchTur = filterTur === 'all' || ih.sozlesme_turu === filterTur;
    return matchSearch && matchAsama && matchTur;
  });
  const SORT_DEFS_ISY = {
    newest: { field: 'created_at', dir: -1 }, oldest: { field: 'created_at', dir: 1 },
    isadi_az: { field: 'is_adi', dir: 1 }, isadi_za: { field: 'is_adi', dir: -1 },
    maliyet_yuksek: { field: 'yaklasik_maliyet', dir: -1, type: 'number' }, maliyet_dusuk: { field: 'yaklasik_maliyet', dir: 1, type: 'number' }
  };
  const sorted = sortByField(filtered, sortKey, SORT_DEFS_ISY);

  const getAsamaLabel = (kod) => ASAMALAR.find(a => a.kod === kod)?.ad || kod;
  const getTurLabel = (t) => SOZLESME_TURLERI.find(s => s.value === t)?.label || t || '-';
  const getUsulLabel = (u) => IHALE_USULLERI.find(i => i.value === u)?.label || u || '-';

  // Conditional fields based on sözleşme süreci
  const isSozlesmeSureci = () => {
    const sozlesmeAsamalar = ['yer_teslimi','insaat_baslangic','insaat_uygulama','hakedisler','gecici_kabul','garanti_suresi','kesin_kabul','teminat_iadesi'];
    return sozlesmeAsamalar.includes(formData.mevcut_asama);
  };

  return (
    <div className="space-y-4" data-testid="isy-ihaleler">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input placeholder="Ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-8 h-9 w-48 bg-gray-800 border-gray-700 text-white text-sm" data-testid="isy-search-input" />
          </div>
          <Select value={filterAsama} onValueChange={setFilterAsama}>
            <SelectTrigger className="h-9 w-40 bg-gray-800 border-gray-700 text-white text-sm"><SelectValue placeholder="Aşama" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-gray-300">Tüm Aşamalar</SelectItem>
              {ASAMALAR.map(a => <SelectItem key={a.kod} value={a.kod} className="text-gray-300">{a.ad}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterTur} onValueChange={setFilterTur}>
            <SelectTrigger className="h-9 w-40 bg-gray-800 border-gray-700 text-white text-sm"><SelectValue placeholder="Tür" /></SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-gray-300">Tüm Türler</SelectItem>
              {SOZLESME_TURLERI.map(t => <SelectItem key={t.value} value={t.value} className="text-gray-300">{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-1 bg-gray-800 rounded-md p-0.5">
            <button onClick={() => setViewMode('list')} className={`px-2 py-1 text-xs rounded ${viewMode === 'list' ? 'bg-gray-600 text-white' : 'text-gray-400'}`}>Liste</button>
            <button onClick={() => setViewMode('pipeline')} className={`px-2 py-1 text-xs rounded ${viewMode === 'pipeline' ? 'bg-gray-600 text-white' : 'text-gray-400'}`}>Pipeline</button>
          </div>
          <SortSelect value={sortKey} onChange={setSortKey} customOptions={[
            { value: 'newest', label: 'En Yeni' }, { value: 'oldest', label: 'En Eski' },
            { value: 'isadi_az', label: 'İş Adı (A-Z)' }, { value: 'isadi_za', label: 'İş Adı (Z-A)' },
            { value: 'maliyet_yuksek', label: 'Maliyet (Yüksek→Düşük)' }, { value: 'maliyet_dusuk', label: 'Maliyet (Düşük→Yüksek)' }
          ]} />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchErpIhaleler(); setAktarDialogOpen(true); }} size="sm" variant="outline" className="bg-emerald-900/30 border-emerald-700/50 text-emerald-400 hover:bg-emerald-900/50" data-testid="isy-erp-aktar-btn">
            <ArrowDownToLine className="w-4 h-4 mr-1" /> İhaleler Modülünden Aktar
          </Button>
          <Button onClick={openCreate} size="sm" className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="isy-new-ihale-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni İhale
          </Button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {sorted.length === 0 ? (
            <Card className="bg-gray-900/60 border-gray-700/50">
              <CardContent className="py-12 text-center text-gray-500">Kayıt bulunamadı</CardContent>
            </Card>
          ) : sorted.map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-gray-600/70 transition-colors" data-testid={`isy-ihale-card-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{ih.is_adi}</span>
                      {ih.ikn && <span className="text-[10px] text-gray-500">IKN: {ih.ikn}</span>}
                      {ih.erp_kaynak_id && <Badge className="bg-emerald-900/30 text-emerald-400 border-emerald-700/40 text-[9px]">İhaleler</Badge>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge style={{ background: ASAMA_COLORS[ih.mevcut_asama] + '33', color: ASAMA_COLORS[ih.mevcut_asama], border: `1px solid ${ASAMA_COLORS[ih.mevcut_asama]}55` }} className="text-[10px] px-1.5 py-0">
                        {getAsamaLabel(ih.mevcut_asama)}
                      </Badge>
                      <span className="text-[10px] text-gray-500">{getTurLabel(ih.sozlesme_turu)}</span>
                      {ih.yaklasik_maliyet && <span className="text-[10px] text-gray-400">{formatMoney(ih.yaklasik_maliyet)}</span>}
                      {ih.sozlesme_bedeli && <span className="text-[10px] text-amber-400">Sözl: {formatMoney(ih.sozlesme_bedeli)}</span>}
                      {ih.yuklenici_adi && <span className="text-[10px] text-gray-500">{ih.yuklenici_adi}</span>}
                      {ih.is_artisi_orani > 0 && (
                        <Badge variant={ih.is_artisi_orani > (ih.sozlesme_turu === 'anahtar_teslim' ? 8 : 15) ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0">
                          Artış: %{ih.is_artisi_orani?.toFixed(1)}
                        </Badge>
                      )}
                      {ih.genel_sartname_uyum_skoru > 0 && (
                        <span className={`text-[10px] ${ih.genel_sartname_uyum_skoru >= 80 ? 'text-green-400' : ih.genel_sartname_uyum_skoru >= 60 ? 'text-amber-400' : 'text-red-400'}`}>
                          Uyum: %{ih.genel_sartname_uyum_skoru}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-400" onClick={() => { setDetailData(ih); setDetailDialog(true); }}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(ih)} data-testid={`isy-edit-${ih.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(ih.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pipeline View */}
      {viewMode === 'pipeline' && (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-3 min-w-max">
            {ASAMALAR.map(asama => {
              const items = filtered.filter(ih => ih.mevcut_asama === asama.kod);
              return (
                <div key={asama.kod} className="w-56 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-2 px-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: ASAMA_COLORS[asama.kod] }} />
                    <span className="text-xs text-gray-400 font-medium truncate">{asama.ad}</span>
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-auto">{items.length}</Badge>
                  </div>
                  <div className="space-y-1.5 min-h-[80px] bg-gray-900/30 rounded-lg p-1.5 border border-gray-800/50">
                    {items.map(ih => (
                      <div key={ih.id} className="bg-gray-800/80 rounded-md p-2 cursor-pointer hover:bg-gray-800 transition-colors border border-gray-700/30" onClick={() => openEdit(ih)}>
                        <p className="text-xs text-white font-medium truncate">{ih.is_adi}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {ih.sozlesme_turu && <span className="text-[9px] text-gray-500">{getTurLabel(ih.sozlesme_turu)}</span>}
                          {ih.yaklasik_maliyet && <span className="text-[9px] text-gray-400 ml-auto">{formatMoney(ih.yaklasik_maliyet)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editMode ? 'İhale Düzenle' : 'Yeni İhale'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {/* Temel Bilgiler */}
            <div className="md:col-span-2"><p className="text-xs text-[#c9a227] font-semibold border-b border-gray-800 pb-1">TEMEL BİLGİLER</p></div>
            <div>
              <Label className="text-gray-400 text-xs">İhale Kayıt No (IKN)</Label>
              <Input value={formData.ikn} onChange={e => setFormData({...formData, ikn: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">İşin Adı *</Label>
              <Input value={formData.is_adi} onChange={e => setFormData({...formData, is_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="isy-form-is-adi" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">İhale Usulü</Label>
              <Select value={formData.ihale_usulu} onValueChange={v => setFormData({...formData, ihale_usulu: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {IHALE_USULLERI.map(u => <SelectItem key={u.value} value={u.value} className="text-gray-300">{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Sözleşme Türü</Label>
              <Select value={formData.sozlesme_turu} onValueChange={v => setFormData({...formData, sozlesme_turu: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {SOZLESME_TURLERI.map(t => <SelectItem key={t.value} value={t.value} className="text-gray-300">{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">İş Grubu</Label>
              <Select value={formData.is_grubu} onValueChange={v => setFormData({...formData, is_grubu: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {IS_GRUPLARI.map(g => <SelectItem key={g.value} value={g.value} className="text-gray-300">{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Mevcut Aşama</Label>
              <Select value={formData.mevcut_asama} onValueChange={v => setFormData({...formData, mevcut_asama: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                  {ASAMALAR.map(a => <SelectItem key={a.kod} value={a.kod} className="text-gray-300">{a.ad}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Mali Bilgiler */}
            <div className="md:col-span-2 mt-2"><p className="text-xs text-[#c9a227] font-semibold border-b border-gray-800 pb-1">MALİ BİLGİLER</p></div>
            <div>
              <Label className="text-gray-400 text-xs">Yaklaşık Maliyet (TL)</Label>
              <Input type="number" value={formData.yaklasik_maliyet} onChange={e => setFormData({...formData, yaklasik_maliyet: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Sözleşme Bedeli (TL)</Label>
              <Input type="number" value={formData.sozlesme_bedeli} onChange={e => setFormData({...formData, sozlesme_bedeli: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>

            {/* Tarihler */}
            <div className="md:col-span-2 mt-2"><p className="text-xs text-[#c9a227] font-semibold border-b border-gray-800 pb-1">TARİHLER</p></div>
            <div>
              <Label className="text-gray-400 text-xs">İhale Tarihi</Label>
              <Input type="date" value={formData.ihale_tarihi} onChange={e => setFormData({...formData, ihale_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Sözleşme Tarihi</Label>
              <Input type="date" value={formData.sozlesme_tarihi} onChange={e => setFormData({...formData, sozlesme_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>

            {/* Sözleşme Süreci Alanları */}
            {isSozlesmeSureci() && (
              <>
                <div className="md:col-span-2 mt-2"><p className="text-xs text-[#c9a227] font-semibold border-b border-gray-800 pb-1">SÖZLEŞME SÜRECİ</p></div>
                <div>
                  <Label className="text-gray-400 text-xs">Yer Teslim Tarihi</Label>
                  <Input type="date" value={formData.yer_teslim_tarihi} onChange={e => setFormData({...formData, yer_teslim_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">İş Başlangıç Tarihi</Label>
                  <Input type="date" value={formData.is_baslangic_tarihi} onChange={e => setFormData({...formData, is_baslangic_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Sözleşme Süresi (Gün)</Label>
                  <Input type="number" value={formData.sozlesme_suresi_gun} onChange={e => setFormData({...formData, sozlesme_suresi_gun: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Orijinal Bitiş Tarihi</Label>
                  <Input type="date" value={formData.orijinal_bitis_tarihi} onChange={e => setFormData({...formData, orijinal_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Revize Bitiş Tarihi</Label>
                  <Input type="date" value={formData.revize_bitis_tarihi} onChange={e => setFormData({...formData, revize_bitis_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Garanti Süresi (Ay)</Label>
                  <Input type="number" value={formData.garanti_suresi_ay} onChange={e => setFormData({...formData, garanti_suresi_ay: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Geçici Kabul Tarihi</Label>
                  <Input type="date" value={formData.gecici_kabul_tarihi} onChange={e => setFormData({...formData, gecici_kabul_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Kesin Kabul Tarihi</Label>
                  <Input type="date" value={formData.kesin_kabul_tarihi} onChange={e => setFormData({...formData, kesin_kabul_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
                </div>
              </>
            )}

            {/* Personel */}
            <div className="md:col-span-2 mt-2"><p className="text-xs text-[#c9a227] font-semibold border-b border-gray-800 pb-1">PERSONEL & YÜKLENİCİ</p></div>
            <div>
              <Label className="text-gray-400 text-xs">Sorumlu Personel</Label>
              <Input value={formData.sorumlu_personel} onChange={e => setFormData({...formData, sorumlu_personel: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Yüklenici Adı</Label>
              <Input value={formData.yuklenici_adi} onChange={e => setFormData({...formData, yuklenici_adi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-xs mb-2 block">Kontrol Teşkilatı (Çoklu Seçim)</Label>
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 max-h-40 overflow-y-auto">
                {users.length === 0 ? (
                  <p className="text-xs text-gray-500">Kullanıcı yükleniyor...</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {users.map(u => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <input type="checkbox" id={`isy-kt-${u.id}`}
                          checked={(formData.kontrol_teskilati || []).includes(u.id)}
                          onChange={(e) => {
                            const current = formData.kontrol_teskilati || [];
                            if (e.target.checked) {
                              setFormData({...formData, kontrol_teskilati: [...current, u.id]});
                            } else {
                              setFormData({...formData, kontrol_teskilati: current.filter(id => id !== u.id)});
                            }
                          }}
                          className="w-3.5 h-3.5 rounded border-gray-600 bg-gray-700" />
                        <label htmlFor={`isy-kt-${u.id}`} className="text-xs text-gray-300 cursor-pointer">{u.name}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(formData.kontrol_teskilati || []).length > 0 && (
                <p className="text-[10px] text-blue-400 mt-1">{(formData.kontrol_teskilati || []).length} kişi seçildi</p>
              )}
            </div>

            {/* Notlar */}
            <div className="md:col-span-2">
              <Label className="text-gray-400 text-xs">Notlar</Label>
              <Textarea value={formData.notlar} onChange={e => setFormData({...formData, notlar: e.target.value})} className="bg-gray-800 border-gray-700 text-white text-sm min-h-[60px]" />
            </div>

            {/* Sözleşme Türüne Göre Bilgi */}
            {formData.sozlesme_turu && (
              <div className="md:col-span-2">
                <div className={`p-3 rounded-lg border ${formData.sozlesme_turu === 'birim_fiyat' ? 'bg-blue-900/20 border-blue-800/30' : formData.sozlesme_turu === 'anahtar_teslim' ? 'bg-purple-900/20 border-purple-800/30' : 'bg-amber-900/20 border-amber-800/30'}`}>
                  <p className="text-xs font-semibold mb-1" style={{ color: formData.sozlesme_turu === 'birim_fiyat' ? '#60a5fa' : formData.sozlesme_turu === 'anahtar_teslim' ? '#a78bfa' : '#fbbf24' }}>
                    {getTurLabel(formData.sozlesme_turu)} Sözleşme Bilgisi
                  </p>
                  {formData.sozlesme_turu === 'birim_fiyat' && (
                    <p className="text-[11px] text-gray-400">
                      İş artışı sınırı: Sözleşme bedelinin %20'si (4735 Md.24/2). Hakediş: Gerçek metraj x Birim fiyat.
                    </p>
                  )}
                  {formData.sozlesme_turu === 'anahtar_teslim' && (
                    <p className="text-[11px] text-gray-400">
                      İş artışı sınırı: Sözleşme bedelinin %10'u (4735 Md.24/1). Hakediş: Pursantaj x Götürü bedel. Uygulama projesi zorunlu.
                    </p>
                  )}
                  {formData.sozlesme_turu === 'karma' && (
                    <p className="text-[11px] text-gray-400">
                      Birim fiyat ve anahtar teslim kısımları ayrı ayrı takip edilir.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="isy-form-save">
              {saving ? 'Kaydediliyor...' : editMode ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{detailData?.is_adi}</DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4 py-2">
              <div className="flex flex-wrap gap-2">
                <Badge style={{ background: ASAMA_COLORS[detailData.mevcut_asama] + '33', color: ASAMA_COLORS[detailData.mevcut_asama] }} className="text-xs">
                  {getAsamaLabel(detailData.mevcut_asama)}
                </Badge>
                {detailData.sozlesme_turu && <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">{getTurLabel(detailData.sozlesme_turu)}</Badge>}
                {detailData.ihale_usulu && <Badge variant="outline" className="text-xs border-gray-600 text-gray-300">{getUsulLabel(detailData.ihale_usulu)}</Badge>}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['IKN', detailData.ikn],
                  ['Yaklaşık Maliyet', formatMoney(detailData.yaklasik_maliyet)],
                  ['Sözleşme Bedeli', formatMoney(detailData.sozlesme_bedeli)],
                  ['Toplam Hakediş', formatMoney(detailData.toplam_hakedis_tutari)],
                  ['İş Artışı', detailData.is_artisi_orani ? `%${detailData.is_artisi_orani.toFixed(1)} (${formatMoney(detailData.is_artisi_tutari)})` : '-'],
                  ['Sözleşme Süresi', detailData.sozlesme_suresi_gun ? `${detailData.sozlesme_suresi_gun} gün` : '-'],
                  ['Yüklenici', detailData.yuklenici_adi],
                  ['Sorumlu', detailData.sorumlu_personel],
                  ['Kontrol Teşkilatı', Array.isArray(detailData.kontrol_teskilati) && detailData.kontrol_teskilati.length > 0 ? detailData.kontrol_teskilati.map(id => { const u = users.find(u => u.id === id); return u ? u.name : id; }).join(', ') : '-'],
                  ['Uyum Skoru', detailData.genel_sartname_uyum_skoru ? `%${detailData.genel_sartname_uyum_skoru}` : '-'],
                  ['İhale Tarihi', detailData.ihale_tarihi],
                  ['Sözleşme Tarihi', detailData.sozlesme_tarihi],
                  ['Yer Teslim', detailData.yer_teslim_tarihi],
                  ['Bitiş Tarihi', detailData.revize_bitis_tarihi || detailData.orijinal_bitis_tarihi],
                ].filter(([, v]) => v && v !== '-').map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm text-gray-200">{val}</p>
                  </div>
                ))}
              </div>
              {detailData.notlar && (
                <div>
                  <p className="text-xs text-gray-500 mb-1">Notlar</p>
                  <p className="text-sm text-gray-300 bg-gray-800/50 p-2 rounded">{detailData.notlar}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">İhaleyi Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu ihaleyi silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* İhaleler Modülünden Aktar Dialog */}
      <Dialog open={aktarDialogOpen} onOpenChange={setAktarDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="erp-aktar-title">İhaleler Modülünden Aktar</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-400 mb-2">İhaleler (Finans) modülündeki kayıtları ISY İhaleler & Sözleşmeler sayfasına aktarın.</p>

          {erpIhaleler.length > 0 && (
            <div className="flex items-center justify-between mb-3 p-2 bg-gray-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Toplam: <span className="text-white font-medium">{erpIhaleler.length}</span></span>
                <span className="text-xs text-emerald-400">Aktarılan: <span className="font-medium">{erpIhaleler.filter(e => aktarilanIds.has(e.id)).length}</span></span>
                <span className="text-xs text-amber-400">Bekleyen: <span className="font-medium">{erpIhaleler.filter(e => !aktarilanIds.has(e.id)).length}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={handleSenkronize} disabled={senkronizeLoading} size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" data-testid="senkronize-btn">
                  <RefreshCw className={`w-3 h-3 mr-1 ${senkronizeLoading ? 'animate-spin' : ''}`} /> {senkronizeLoading ? 'Güncelleniyor...' : 'Verileri Güncelle'}
                </Button>
                <Button onClick={handleTopluAktar} disabled={topluAktarLoading} size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid="toplu-aktar-btn">
                  <ArrowDownToLine className="w-3 h-3 mr-1" /> {topluAktarLoading ? 'Aktarılıyor...' : 'Tümünü Aktar'}
                </Button>
              </div>
            </div>
          )}

          {erpIhaleler.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p className="text-sm">İhaleler modülünde kayıt bulunamadı.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Aktarılmamış İhaleler */}
              {erpIhaleler.filter(e => !aktarilanIds.has(e.id)).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Aktarılmamış İhaleler ({erpIhaleler.filter(e => !aktarilanIds.has(e.id)).length})
                  </p>
                  <div className="space-y-1.5">
                    {erpIhaleler.filter(e => !aktarilanIds.has(e.id)).map(e => (
                      <div key={e.id} className="p-3 rounded-lg border bg-gray-800/50 border-gray-700/50 hover:border-emerald-700/50 transition-colors">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{e.isin_adi}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                              {e.ihale_no && <span className="text-blue-400">IKN: {e.ihale_no}</span>}
                              <span>{e.yil}</span>
                              {e.ilcesi && <span>{e.ilcesi}</span>}
                              {e.durumu && <Badge className="bg-gray-700 text-[10px]">{e.durumu}</Badge>}
                              {e.ihaleyi_alan_firma && <span className="text-gray-500">{e.ihaleyi_alan_firma}</span>}
                            </div>
                          </div>
                          <Button size="sm" onClick={() => handleTekAktar(e.id)} disabled={aktarmaLoading}
                            className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`aktar-erp-${e.id}`}>
                            <ArrowDownToLine className="w-3 h-3 mr-1" /> Aktar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aktarılmış İhaleler */}
              {erpIhaleler.filter(e => aktarilanIds.has(e.id)).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Aktarılmış İhaleler ({erpIhaleler.filter(e => aktarilanIds.has(e.id)).length})
                  </p>
                  <div className="space-y-1.5">
                    {erpIhaleler.filter(e => aktarilanIds.has(e.id)).map(e => (
                      <div key={e.id} className="p-2.5 rounded-lg border bg-green-900/10 border-green-800/30">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-300 truncate">{e.isin_adi}</p>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                              {e.ihale_no && <span>IKN: {e.ihale_no}</span>}
                              <span>{e.yil}</span>
                              {e.ilcesi && <span>{e.ilcesi}</span>}
                            </div>
                          </div>
                          <Badge className="bg-green-800 text-green-200 text-[10px] flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Aktarıldı
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setAktarDialogOpen(false)} className="bg-gray-700 hover:bg-gray-600">Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
